import React, { useEffect, useState } from 'react';
import { Boxes, Car, Eye, EyeOff, Pill, ShieldCheck, ShoppingBasket, Store, UtensilsCrossed, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { APPLICATION_NAME } from '../constants/AppConfig';
import { parseApiError } from '../utils/errorUtils';
import AppLogo from '../images/logo.png';
import SmartSoftLogo from '../images/smartsoft.png';
import './LoginPage.css';
import './RegisterPage.css';

interface ModuleInfo {
  code: string;
  displayName: string;
  description: string;
}

interface FormData {
  companyName: string;
  moduleCode: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface FieldErrors {
  companyName?: string;
  moduleCode?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

const MAX_LENGTHS = {
  companyName: 150,
  firstName: 50,
  lastName: 50,
  email: 150,
  password: 128,
  confirmPassword: 128,
};

const MODULE_ICONS: Record<string, React.ReactNode> = {
  retail: <Store size={20} strokeWidth={1.75} />,
  grocery: <ShoppingBasket size={20} strokeWidth={1.75} />,
  pharmacy: <Pill size={20} strokeWidth={1.75} />,
  hardware: <Wrench size={20} strokeWidth={1.75} />,
  restaurant: <UtensilsCrossed size={20} strokeWidth={1.75} />,
  automobile: <Wrench size={20} strokeWidth={1.75} />,
  carRental: <Car size={20} strokeWidth={1.75} />,
  general: <Boxes size={20} strokeWidth={1.75} />,
};

// Shown for any module code the icon map does not know about
const DEFAULT_MODULE_ICON: React.ReactNode = <Boxes size={20} strokeWidth={1.75} />;

/**
 * Public self-service company signup (demo flow). The module choice is
 * permanent — the backend has no endpoint that changes it after registration.
 */
const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [modules, setModules] = useState<ModuleInfo[]>([]);
  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    moduleCode: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiService
      .get<ModuleInfo[]>('/companies/modules')
      .then((res) => setModules(res.data.data ?? []))
      .catch(() => setModules([]));
  }, []);

  const selectedModule = modules.find((m) => m.code === formData.moduleCode);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    let finalValue = value;
    if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
      finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    setError(null);
  };

  const handleModuleSelect = (code: string) => {
    setFormData((prev) => ({ ...prev, moduleCode: code }));
    setFieldErrors((prev) => ({ ...prev, moduleCode: undefined }));
    setError(null);
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    if (formData.companyName.trim().length < 3) {
      errors.companyName = t('register.companyNameRequired');
      isValid = false;
    } else if (formData.companyName.length > MAX_LENGTHS.companyName) {
      errors.companyName = t('register.companyNameRequired');
      isValid = false;
    }

    if (!formData.moduleCode) {
      errors.moduleCode = t('register.moduleRequired');
      isValid = false;
    }

    if (!formData.firstName.trim()) {
      errors.firstName = t('register.firstNameRequired');
      isValid = false;
    }

    if (!formData.lastName.trim()) {
      errors.lastName = t('register.lastNameRequired');
      isValid = false;
    }

    if (!formData.email.trim() || !formData.email.includes('@') || formData.email.length > MAX_LENGTHS.email) {
      errors.email = t('register.emailRequired');
      isValid = false;
    }

    if (formData.password.length < 8) {
      errors.password = t('register.passwordRequired');
      isValid = false;
    }

    if (formData.confirmPassword !== formData.password) {
      errors.confirmPassword = t('register.passwordMismatch');
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await apiService.post('/companies/register', {
        companyName: formData.companyName.trim(),
        moduleCode: formData.moduleCode,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        adminEmail: formData.email.trim(),
        adminPassword: formData.password,
      });

      if (response.data.success) {
        navigate('/login', { state: { registered: true } });
      }
    } catch (err: unknown) {
      setError(parseApiError(err, t('register.failed')).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-page">
      <div className="register-card">
        {/* ── Brand / module summary panel ── */}
        <div className="register-brand">
          <img src={AppLogo} alt={APPLICATION_NAME} className="register-form-logo" />
          <span className="register-brand-app">{APPLICATION_NAME}</span>
          <h1 className="register-brand-title">{t('register.title')}</h1>
          <p className="register-brand-tagline">{t('register.tagline')}</p>

          {selectedModule && (
            <div className="register-brand-module">
              <span className="register-brand-module-icon">
                {MODULE_ICONS[selectedModule.code] ?? DEFAULT_MODULE_ICON}
              </span>
              <div>
                <p className="register-brand-module-name">{selectedModule.displayName}</p>
                <p className="register-brand-module-desc">{selectedModule.description}</p>
              </div>
            </div>
          )}
          <div className="register-brand-lock">
            <img src={SmartSoftLogo} alt={APPLICATION_NAME} className="register-form-company_logo" />
          </div>
          <div className="register-brand-lock">
            <ShieldCheck color="white" size={14} strokeWidth={2} /> 
            <span>
              {t('register.moduleLockNotice')}
            </span>
          </div>
        </div>

        {/* ── Form panel ── */}
        <div className="register-form-panel">
         

          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <strong>{t('common.error')}:</strong> {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
                aria-label={t('common.close')}
              ></button>
            </div>
          )}

          <form onSubmit={handleRegister} className="register-form" noValidate>
            <p className="register-section-label">{t('register.companyDetails')}</p>

            <div className="form-group">
              <label className="form-label" htmlFor="reg-company">{t('register.companyName')}</label>
              <input
                id="reg-company"
                type="text"
                name="companyName"
                className={`form-control${fieldErrors.companyName ? ' is-invalid' : ''}`}
                placeholder={t('register.companyNamePlaceholder')}
                value={formData.companyName}
                onChange={handleInputChange}
                disabled={loading}
                autoFocus
                maxLength={MAX_LENGTHS.companyName}
              />
              {fieldErrors.companyName && <small className="form-error">{fieldErrors.companyName}</small>}
            </div>

            <div className="form-group">
              <label className="form-label">{t('register.module')}</label>
              <div className={`register-modules${fieldErrors.moduleCode ? ' is-invalid' : ''}`} role="radiogroup">
                {modules.map((m) => (
                  <button
                    key={m.code}
                    type="button"
                    role="radio"
                    aria-checked={formData.moduleCode === m.code}
                    className={`register-module-chip${formData.moduleCode === m.code ? ' selected' : ''}`}
                    onClick={() => handleModuleSelect(m.code)}
                    disabled={loading}
                  >
                    {MODULE_ICONS[m.code] ?? DEFAULT_MODULE_ICON}
                    <span>{m.displayName}</span>
                  </button>
                ))}
              </div>
              {fieldErrors.moduleCode && <small className="form-error">{fieldErrors.moduleCode}</small>}
            </div>

            <p className="register-section-label">{t('register.adminAccount')}</p>

            <div className="register-grid-2">
              <div className="form-group">
                <label className="form-label" htmlFor="reg-firstname">{t('register.firstName')}</label>
                <input
                  id="reg-firstname"
                  type="text"
                  name="firstName"
                  className={`form-control${fieldErrors.firstName ? ' is-invalid' : ''}`}
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={loading}
                  maxLength={MAX_LENGTHS.firstName}
                />
                {fieldErrors.firstName && <small className="form-error">{fieldErrors.firstName}</small>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="reg-lastname">{t('register.lastName')}</label>
                <input
                  id="reg-lastname"
                  type="text"
                  name="lastName"
                  className={`form-control${fieldErrors.lastName ? ' is-invalid' : ''}`}
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={loading}
                  maxLength={MAX_LENGTHS.lastName}
                />
                {fieldErrors.lastName && <small className="form-error">{fieldErrors.lastName}</small>}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="reg-email">{t('register.email')}</label>
              <input
                id="reg-email"
                type="email"
                name="email"
                className={`form-control${fieldErrors.email ? ' is-invalid' : ''}`}
                value={formData.email}
                onChange={handleInputChange}
                disabled={loading}
                autoComplete="email"
                maxLength={MAX_LENGTHS.email}
              />
              {fieldErrors.email && <small className="form-error">{fieldErrors.email}</small>}
            </div>

            <div className="register-grid-2">
              <div className="form-group">
                <label className="form-label" htmlFor="reg-password">{t('login.password')}</label>
                <div className="password-input-wrapper">
                  <input
                    id="reg-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className={`form-control${fieldErrors.password ? ' is-invalid' : ''}`}
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={loading}
                    autoComplete="new-password"
                    maxLength={MAX_LENGTHS.password}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {fieldErrors.password && <small className="form-error">{fieldErrors.password}</small>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="reg-confirm">{t('register.confirmPassword')}</label>
                <input
                  id="reg-confirm"
                  type="password"
                  name="confirmPassword"
                  className={`form-control${fieldErrors.confirmPassword ? ' is-invalid' : ''}`}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  disabled={loading}
                  autoComplete="new-password"
                  maxLength={MAX_LENGTHS.confirmPassword}
                />
                {fieldErrors.confirmPassword && <small className="form-error">{fieldErrors.confirmPassword}</small>}
              </div>
            </div>

            <button type="submit" className="btn-save register-submit" disabled={loading}>
              {loading ? t('register.creating') : t('register.create')}
            </button>
          </form>

          <div className="register-footer">
            <button
              type="button"
              className="forgot-password btn btn-link p-0"
              onClick={() => navigate('/login')}
            >
              {t('register.backToLogin')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
